/**
 * Coach-side KoT match result validation and submit payload builders.
 * Mirrors rules in KingOfTableRuntimePanel / kingOfTable.aplication.
 */

export function normalizeKotMode(mode) {
  if (mode === "FULL_SET") return "FULL_SET";
  if (mode === "STOP_SCORE_LEAD" || mode === "STOP_ON_FIRST") return "STOP_SCORE_LEAD";
  return "STOP_SCORE_LEAD";
}

export function isRealKotMatchId(kotMatchId) {
  if (!kotMatchId) return false;
  const id = String(kotMatchId);
  return !id.startsWith("setup-") && !id.startsWith("pending-");
}

/**
 * Coach can open the result wizard on an active table during the current round.
 */
export function canCoachShowResultButton({ match, roundState }) {
  if (!match || match.match_state !== "IN_PROGRESS") return false;
  if (!isRealKotMatchId(match.kot_match_id)) return false;
  return roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED";
}

/**
 * STOP_SCORE_LEAD: coach must request round stop before scores can be submitted.
 */
export function coachMustStopRoundBeforeResult({ config, roundState }) {
  const mode = normalizeKotMode(config?.match_winner_mode || config?.kot_mode);
  return mode === "STOP_SCORE_LEAD" && roundState === "IN_PROGRESS";
}

export function coachResultButtonLabelKey({ config, roundState }) {
  return coachMustStopRoundBeforeResult({ config, roundState })
    ? "kotCoachStopAndEnterResult"
    : "kotCoachEnterResult";
}

/**
 * @param {object} params
 * @param {object} params.match
 * @param {object} [params.config] — snapshot.config
 * @param {string|null} [params.roundState] — current_round.round_state
 */
export function canCoachEnterResultForMatch({ match, config, roundState }) {
  if (!canCoachShowResultButton({ match, roundState })) return false;

  const mode = normalizeKotMode(config?.match_winner_mode || config?.kot_mode);
  if (mode === "FULL_SET") {
    return true;
  }
  return roundState === "STOP_REQUESTED";
}

/**
 * Player may enter result for their own match only (same timing rules as coach).
 */
export function canPlayerEnterResultForMatch({
  match,
  config,
  roundState,
  viewerPlayerId,
}) {
  if (!viewerPlayerId) return false;
  const pid = String(viewerPlayerId);
  const involved =
    (match?.player_a_id != null && String(match.player_a_id) === pid) ||
    (match?.player_b_id != null && String(match.player_b_id) === pid);
  if (!involved) return false;
  return canCoachEnterResultForMatch({ match, config, roundState });
}

/**
 * Whether coach can open the result dialog (may still need golden-point pick on submit).
 */
export function coachResultEntryBlockedHint({ config, roundState }) {
  const mode = normalizeKotMode(config?.match_winner_mode || config?.kot_mode);
  if (mode === "FULL_SET") return null;
  if (roundState === "IN_PROGRESS") return "kotResultsAfterRoundStop";
  return null;
}

export function deriveCoachSubmitPayload(match, config, scoreA, scoreB) {
  const mode = normalizeKotMode(config?.match_winner_mode || config?.kot_mode);
  const roundState = config?.round_state;
  const tieRule = config?.tie_break_rule || "GOLDEN_POINT";
  const a = Number(scoreA);
  const b = Number(scoreB);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const playerAId = match.player_a_id;
  const playerBId = match.player_b_id;
  if (!playerAId || !playerBId) return null;

  if (mode === "FULL_SET") {
    if (a === b) return null;
    return {
      decided_by: "FULL_SET",
      winner_player_id: a > b ? playerAId : playerBId,
      player_a_score: a,
      player_b_score: b,
    };
  }

  if (roundState !== "STOP_REQUESTED") return null;
  if (a === b) {
    if (tieRule !== "GOLDEN_POINT") return null;
    return null;
  }
  return {
    decided_by: "STOP_SCORE_LEAD",
    winner_player_id: a > b ? playerAId : playerBId,
    player_a_score: a,
    player_b_score: b,
  };
}

/**
 * Golden-point winner when scores are tied at round stop.
 * @param {"A"|"B"} goldenWinnerSide
 */
export function deriveCoachGoldenPointPayload(match, goldenWinnerSide, score) {
  const tied = Number(score);
  if (!Number.isFinite(tied)) return null;
  const playerAId = match.player_a_id;
  const playerBId = match.player_b_id;
  if (!playerAId || !playerBId) return null;
  return {
    decided_by: "GOLDEN_POINT",
    winner_player_id:
      goldenWinnerSide === "A" ? playerAId : playerBId,
    player_a_score: tied,
    player_b_score: tied,
  };
}

/**
 * Resolve submit body from scores + optional golden-point side.
 */
export function buildCoachSubmitBody(match, config, scoreA, scoreB, goldenWinnerSide = "A") {
  const body = deriveCoachSubmitPayload(match, config, scoreA, scoreB);
  if (body) return body;

  const roundState = config?.round_state;
  const tieRule = config?.tie_break_rule || "GOLDEN_POINT";
  const a = Number(scoreA);
  const b = Number(scoreB);

  if (
    roundState === "STOP_REQUESTED" &&
    a === b &&
    tieRule === "GOLDEN_POINT"
  ) {
    return deriveCoachGoldenPointPayload(match, goldenWinnerSide, scoreA);
  }
  return null;
}

export function needsGoldenPointPicker(config, scoreA, scoreB) {
  const roundState = config?.round_state;
  const tieRule = config?.tie_break_rule || "GOLDEN_POINT";
  const a = Number(scoreA);
  const b = Number(scoreB);
  return (
    roundState === "STOP_REQUESTED" &&
    Number.isFinite(a) &&
    Number.isFinite(b) &&
    a === b &&
    tieRule === "GOLDEN_POINT"
  );
}
